import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, Plus, Globe, ShieldCheck } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceAdvancedStrategy,
  useServiceAdvancedEnabledSites,
  useCreateServiceAdvancedStrategy,
  useUpdateServiceAdvancedStrategy,
  ADVANCED_TYPE_XML_PROTECTION,
  useServiceStaticFiles,
  useUploadServiceStaticFile,
  type AdvancedStrategy,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './XMLProtectionForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;
const TYPE = ADVANCED_TYPE_XML_PROTECTION;

/** The Basic-XML numeric limits: each is a `<key>` value gated by a `<key>_enabled`
 *  checkbox. `def` is the vendor's default. */
const MAX_FIELDS = [
  { key: 'max_xml_depth', label: 'Max Tree Depth', def: 20 },
  { key: 'max_elem_name_length', label: 'Max Element Name Length', def: 64 },
  { key: 'max_elem_count', label: 'Max Element Count', def: 10000 },
  { key: 'max_child_node_count', label: 'Max Child Node Count', def: 100 },
  { key: 'max_attr_count', label: 'Max Attribute Count', def: 32 },
  { key: 'max_attr_name_length', label: 'Max Attribute Name Length', def: 64 },
  { key: 'max_attr_value_length', label: 'Max Attribute Value Length', def: 1024 },
  { key: 'max_cdata_length', label: 'Max CDATA Length', def: 4 },
  { key: 'max_xml_size', label: 'Max Document Size', def: 16000 },
  { key: 'min_xml_size', label: 'Min Document Size', def: 4 },
] as const;

/** The three "Disallow" toggles (default on). */
const BAN_FIELDS = [
  { key: 'ban_proc_instructions', label: 'Disallow Processing Instructions' },
  { key: 'ban_dtds', label: 'Disallow DTDs' },
  { key: 'ban_external_entities', label: 'Disallow External Entities' },
] as const;

const VERIFICATIONS = [
  { value: 'base', label: 'Basic XML Verification' },
  { value: 'schema', label: 'Schema Verification' },
  { value: 'soap', label: 'SOAP Verification' },
] as const;

type Verification = (typeof VERIFICATIONS)[number]['value'];

/** The XML Protection (type 5) per-type config (Basic-XML subset; schema/soap
 *  fields are passed through as empty defaults until those tabs are wired). */
type XMLConfig = Record<string, unknown>;

interface FormState {
  name: string;
  description: string;
  sites: number[];
  verification: Verification;
  values: Record<string, number>;
  enabled: Record<string, boolean>;
  bans: Record<string, boolean>;
  /** Schema / SOAP file-verification entries — opaque passthrough (the upload +
   *  entry shape aren't wired yet, so they round-trip unchanged on edit). */
  xmlSchemaList: unknown[];
  xmlSoapList: unknown[];
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  sites: [],
  verification: 'base',
  values: Object.fromEntries(MAX_FIELDS.map((f) => [f.key, f.def])),
  enabled: Object.fromEntries(MAX_FIELDS.map((f) => [f.key, false])),
  bans: Object.fromEntries(BAN_FIELDS.map((f) => [f.key, true])),
  xmlSchemaList: [],
  xmlSoapList: [],
});

/**
 * Add / Edit an XML Protection rule (AI-WAF → Advanced Protection,
 * waf_advanced_strategy type 5). Only Basic XML Verification is wired; Schema /
 * SOAP Verification are placeholders. Builds the per-type `config`; the BE adds
 * the common envelope keys. Routes: aiwaf/advanced/xml-protection/new + /:id.
 */
export function XMLProtectionForm() {
  const navigate = useNavigate();
  const { serviceId, id } = useParams<{ serviceId: string; id: string }>();
  const isEdit = !!id;
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;


  const { data: existing, isLoading: loadingExisting } = useServiceAdvancedStrategy<XMLConfig>(
    serviceId,
    TYPE,
    id,
  );
  const { data: sites = [] } = useServiceAdvancedEnabledSites(serviceId);

  const [form, setForm] = useState<FormState>(emptyForm);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!existing) return;
    const c = (existing.config ?? {}) as XMLConfig;
    const num = (k: string, d: number) => (typeof c[k] === 'number' ? (c[k] as number) : d);
    const bool = (k: string) => c[k] === true;
    const v = (c.type as Verification) || 'base';
    setForm({
      name: existing.name ?? '',
      description: existing.description ?? '',
      sites: existing.sites ?? [],
      verification: v === 'schema' || v === 'soap' ? v : 'base',
      values: Object.fromEntries(MAX_FIELDS.map((f) => [f.key, num(f.key, f.def)])),
      enabled: Object.fromEntries(MAX_FIELDS.map((f) => [f.key, bool(`${f.key}_enabled`)])),
      bans: Object.fromEntries(
        BAN_FIELDS.map((f) => [f.key, c[f.key] === undefined ? true : bool(f.key)]),
      ),
      xmlSchemaList: Array.isArray(c.xml_schema_list) ? (c.xml_schema_list as unknown[]) : [],
      xmlSoapList: Array.isArray(c.xml_soap_list) ? (c.xml_soap_list as unknown[]) : [],
    });
  }, [existing]);

  const siteSet = useMemo(() => new Set(form.sites), [form.sites]);
  const toggleSite = (sid: number) =>
    set('sites', siteSet.has(sid) ? form.sites.filter((x) => x !== sid) : [...form.sites, sid]);

  const create = useCreateServiceAdvancedStrategy<XMLConfig>(serviceId ?? '', TYPE, {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'xml_protection_rule', 'create');
      show({ tone: 'success', title: 'XML Protection rule created' });
      navigate(`${base}/aiwaf/advanced?tab=xml-protection`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateServiceAdvancedStrategy<XMLConfig>(serviceId ?? '', TYPE, id ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'xml_protection_rule', 'edit');
      show({ tone: 'success', title: 'XML Protection rule saved' });
      navigate(`${base}/aiwaf/advanced?tab=xml-protection`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });
  const saving = create.isPending || update.isPending;

  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX;
  const canSubmit = nameValid && !saving;

  const submit = () => {
    const v = form.verification;
    // The basic-XML fields are always sent; the verification method only switches
    // `type` + the three is_*_enabled flags (mirrors the captured payloads).
    const config: XMLConfig = {
      type: v,
      is_basexml_enabled: v === 'base',
      is_schema_enabled: v === 'schema',
      is_soap_enabled: v === 'soap',
      xml_schema_list: form.xmlSchemaList,
      xml_soap_list: form.xmlSoapList,
    };
    for (const f of MAX_FIELDS) {
      config[f.key] = form.values[f.key];
      config[`${f.key}_enabled`] = form.enabled[f.key];
    }
    for (const f of BAN_FIELDS) config[f.key] = form.bans[f.key];

    const payload: AdvancedStrategy<XMLConfig> = {
      type: TYPE,
      name: form.name.trim(),
      description: form.description.trim(),
      sites: form.sites,
      config,
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  if (isEdit && loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton className={styles.skelForm} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'AI-WAF' },
              { label: 'Advanced Protection', slug: 'aiwaf/advanced' },
              { label: isEdit ? 'Edit XML Protection Rule' : 'XML Protection Rule' },
            ]}
          />
        }
        title="XML Protection Rule"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>General</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Name"
                required
                description={`1-${NAME_MAX} characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +`}
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    maxLength={NAME_MAX}
                    placeholder="Rule name"
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Description" description={`Up to ${DESC_MAX} characters.`}>
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.description}
                      maxLength={DESC_MAX}
                      placeholder="Description"
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.description.length}/{DESC_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Triggering Condition</CardTitle>
              <CardDescription>Choose a verification method: Basic XML (size/structure limits), Schema (XSD), or SOAP (WSDL).</CardDescription>
            </CardHeader>
            <CardBody>
            {/* Verification method radio */}
            <div className={styles.methodRow}>
              {VERIFICATIONS.map((v) => (
                <Button
                  key={v.value}
                  variant={form.verification === v.value ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => set('verification', v.value)}
                >
                  {v.label}
                </Button>
              ))}
            </div>

            {form.verification !== 'base' ? (
              <FileVerificationPanel
                key={form.verification}
                serviceId={serviceId ?? ''}
                fileType={form.verification === 'schema' ? 'xml_protection_schema' : 'xml_protection_soap'}
                title={form.verification === 'schema' ? 'Schema Verification' : 'SOAP Verification'}
                accept={form.verification === 'schema' ? '.xsd' : '.wsdl,.xml'}
                pathKey={form.verification === 'schema' ? 'schema_url_path' : 'soap_url_path'}
                fileKey={form.verification === 'schema' ? 'schema_file_name' : 'soap_file_name'}
                entries={form.verification === 'schema' ? form.xmlSchemaList : form.xmlSoapList}
                onChange={(next) =>
                  set(form.verification === 'schema' ? 'xmlSchemaList' : 'xmlSoapList', next)
                }
              />
            ) : (
              <div className={styles.box}>
                {/* Numeric limits grid */}
                <div className={styles.numGrid}>
                  {MAX_FIELDS.map((f) => (
                    <div key={f.key}>
                      <label className={styles.fieldLabel}>
                        <Checkbox
                          checked={form.enabled[f.key]}
                          onCheckedChange={(v) =>
                            set('enabled', { ...form.enabled, [f.key]: !!v })
                          }
                        />
                        <span className={styles.bold}>{f.label}</span>
                      </label>
                      <Input
                        type="number"
                        min={0}
                        value={String(form.values[f.key])}
                        onChange={(e) =>
                          set('values', { ...form.values, [f.key]: Number(e.target.value) })
                        }
                      />
                    </div>
                  ))}
                </div>

                {/* Disallow toggles */}
                <div className={styles.banGrid}>
                  {BAN_FIELDS.map((f) => (
                    <label key={f.key} className={styles.checkLabel}>
                      <Checkbox
                        checked={form.bans[f.key]}
                        onCheckedChange={(v) => set('bans', { ...form.bans, [f.key]: !!v })}
                      />
                      <span>{f.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Apply to</CardTitle>
              <CardDescription>Select the websites this rule protects.</CardDescription>
            </CardHeader>
            <CardBody>
            {sites.length === 0 ? (
              <EmptyState icon={<Globe size={20} />} title="No websites available" description="Enable WAAP protection on a website first, then apply this rule." />
            ) : (
              <div className={styles.siteList}>
                {sites.map((s) => (
                  <label key={s.id} className={styles.siteLabel}>
                    <Checkbox checked={siteSet.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                    <span>{s.domain}</span>
                  </label>
                ))}
              </div>
            )}
            </CardBody>
          </Card>

          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/advanced?tab=xml-protection`)}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={submit}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
    </Container>
  );
}

/** One Schema/SOAP verification entry. The vendor key names differ per method:
 *  schema → { schema_url_path, schema_file_name }; soap → { soap_url_path,
 *  soap_file_name } (the panel is told which via pathKey/fileKey). */
type FileEntry = Record<string, string>;

/** Schema / SOAP verification panel. Two parts:
 *  1. Upload File → POST /static_file/upload (type xml_protection_schema/soap);
 *     uploaded files populate the File picker below.
 *  2. Verification entries → the rule's xml_schema_list / xml_soap_list: each row
 *     pairs a Path with a selected uploaded File. Add / Edit / Delete build the
 *     list (saved with the rule).
 *  NOTE: entries are stored as { path, file: <filename> } — matching the table's
 *  display. If the vendor's rule expects a different shape (e.g. the file url/id),
 *  share a rule save payload and adjust. */
function FileVerificationPanel({
  serviceId,
  fileType,
  title,
  accept,
  pathKey,
  fileKey,
  entries,
  onChange,
}: {
  serviceId: string;
  fileType: string;
  title: string;
  accept: string;
  pathKey: string;
  fileKey: string;
  entries: unknown[];
  onChange: (next: FileEntry[]) => void;
}) {
  const { show } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: files = [] } = useServiceStaticFiles(serviceId, fileType);
  const upload = useUploadServiceStaticFile(serviceId, {
    onSuccess: () => show({ tone: 'success', title: 'File uploaded' }),
    onError: (e) => show({ tone: 'danger', title: 'Upload failed', description: errMessage(e) }),
  });

  const list = (entries as FileEntry[]) ?? [];
  const [draftPath, setDraftPath] = useState('');
  const [draftFile, setDraftFile] = useState('');
  const [editIdx, setEditIdx] = useState<number | null>(null);

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    const content = await file.text();
    upload.mutate({ content, name: file.name, type: fileType });
  };

  const addEntry = () => {
    if (!draftPath.trim() || !draftFile) return;
    const entry: FileEntry = { [pathKey]: draftPath.trim(), [fileKey]: draftFile };
    onChange(editIdx === null ? [...list, entry] : list.map((e, i) => (i === editIdx ? entry : e)));
    setDraftPath('');
    setDraftFile('');
    setEditIdx(null);
  };
  const editEntry = (i: number) => {
    setDraftPath(list[i][pathKey] ?? '');
    setDraftFile(list[i][fileKey] ?? '');
    setEditIdx(i);
  };
  const delEntry = (i: number) => onChange(list.filter((_, idx) => idx !== i));

  const cols = '2fr 2fr auto';
  return (
    <div className={styles.box}>
      {/* Upload File */}
      <div>
        <div className={styles.subTitle}>Upload File</div>
        <div className={styles.uploadDesc}>
          {title === 'Schema Verification' ? 'Upload an XSD schema. ' : 'Upload a WSDL definition. '}
          The file name can contain 1-64 characters: A-Z, a-z, 0-9, _ (underline) and . (dot).
        </div>
        <input ref={fileInputRef} type="file" accept={accept} style={{ display: 'none' }} onChange={onPick} />
        <Button
          variant="secondary"
          startIcon={<Upload size={14} />}
          disabled={upload.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          {upload.isPending ? 'Uploading…' : 'Upload'}
        </Button>
      </div>

      {/* Verification entries (path ↔ uploaded file) */}
      <div>
        <div className={styles.entryHead}>
          <div className={styles.bold}>{title}</div>
          <Button
            variant="secondary"
            size="sm"
            startIcon={<Plus size={14} />}
            disabled={!draftPath.trim() || !draftFile}
            onClick={addEntry}
          >
            {editIdx === null ? `Add ${title}` : 'Save Entry'}
          </Button>
        </div>
        <div className={styles.entryRow}>
          <Field label="Path" required>
            {(props) => (
              <Input
                {...props}
                value={draftPath}
                maxLength={512}
                placeholder="Path"
                className={styles.pathInput}
                onChange={(e) => setDraftPath(e.target.value)}
              />
            )}
          </Field>
          <Field label="File" required>
            {() => (
              <Select value={draftFile} onValueChange={setDraftFile}>
                <SelectTrigger className={styles.fileInput}>
                  <SelectValue placeholder="Select a File" />
                </SelectTrigger>
                <SelectContent>
                  {files.map((f) => (
                    <SelectItem key={f.id} value={f.name}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        </div>

        <div className={styles.table}>
          <div className={styles.tableHead} style={{ gridTemplateColumns: cols }}>
            <div className={styles.cell}>Path</div>
            <div className={styles.cell}>File</div>
            <div className={styles.cell}>Operation</div>
          </div>
          {list.length === 0 ? (
            <EmptyState icon={<ShieldCheck size={20} />} title="No entries yet" description="Add path-file pairs to configure XML protection exceptions." />
          ) : (
            list.map((r, i) => (
              <div key={i} className={styles.tableRow} style={{ gridTemplateColumns: cols }}>
                <div className={styles.cell}>{r[pathKey] || '—'}</div>
                <div className={styles.cell}>{r[fileKey] || '—'}</div>
                <div className={styles.cell}>
                  <Button variant="ghost" size="sm" onClick={() => editEntry(i)}>
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => delEntry(i)}>
                    Delete
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
